/**
 * Cross-tab authentication sync over a BroadcastChannel.
 *
 * keycloak-js keeps its tokens in memory per tab, so without this every new tab
 * redirects through Keycloak's authorization endpoint to obtain its own tokens.
 * Each of those redirects fires a Keycloak LOGIN event, which the login-attempts
 * listener records as a fresh login row. By letting a new tab pull the current
 * tokens from an already-open tab, the new tab initializes Keycloak with those
 * tokens and only ever *refreshes* them (a REFRESH_TOKEN event, ignored by the
 * listener) — no redirect, no spurious login row.
 *
 * The helpers here are deliberately framework-free so the pre-bootstrap
 * requester (main.ts, before Angular DI exists) and the in-app responder
 * (AuthSyncService) can share one protocol.
 */

export const AUTH_SYNC_CHANNEL = 'nrg-auth-sync';

/** The keycloak-js token triplet shared between tabs. */
export interface SharedTokens {
  token: string;
  refreshToken: string;
  idToken?: string;
}

/** A new tab asking open tabs for their current tokens. */
export interface RequestTokensMessage {
  type: 'request-tokens';
  /** Correlates a reply to this specific request. */
  nonce: string;
}

/** An open tab answering a {@link RequestTokensMessage}. */
export interface TokensMessage {
  type: 'tokens';
  nonce: string;
  tokens: SharedTokens;
}

/** A tab signalling that the user logged out, so peers should follow. */
export interface LogoutMessage {
  type: 'logout';
}

export type AuthSyncMessage = RequestTokensMessage | TokensMessage | LogoutMessage;

/**
 * Ask any already-open tab for its current tokens.
 *
 * Resolves with the first peer's tokens, or `null` if no peer answers within
 * `timeoutMs` (e.g. this is the only/first tab) — in which case the caller
 * should fall back to the normal `login-required` flow.
 */
export function requestTokensFromPeers(timeoutMs = 400): Promise<SharedTokens | null> {
  // BroadcastChannel is unavailable in very old browsers; degrade to no-inherit.
  if (typeof BroadcastChannel === 'undefined') {
    return Promise.resolve(null);
  }

  return new Promise<SharedTokens | null>(resolve => {
    const channel = new BroadcastChannel(AUTH_SYNC_CHANNEL);
    const nonce = crypto.randomUUID();
    let settled = false;

    const finish = (result: SharedTokens | null) => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timer);
      channel.removeEventListener('message', onMessage);
      channel.close();
      resolve(result);
    };

    const onMessage = (event: MessageEvent<AuthSyncMessage>) => {
      const message = event.data;
      if (message?.type === 'tokens' && message.nonce === nonce && message.tokens?.refreshToken) {
        finish(message.tokens);
      }
    };

    const timer = setTimeout(() => finish(null), timeoutMs);
    channel.addEventListener('message', onMessage);
    channel.postMessage({ type: 'request-tokens', nonce } satisfies RequestTokensMessage);
  });
}
